import React, { useState } from 'react';
import { Plus, Edit, Trash2, Download, Eye, CalendarIcon, Euro, FileSpreadsheet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FormDialog, FormField } from '@/components/shared/FormDialog';
import { useRoomTypes } from '@/hooks/useRoomTypes';
import { useRooms } from '@/hooks/useRooms';
import { useRates } from '@/hooks/useRates';
import { formatCurrency, formatDate } from '@/utils/format';
import { toast } from 'sonner';
import { apiService } from '@/services/api';

/**
 * Rates Management Component
 * Manage room pricing with Beds24 synchronization
 */
export const RatesManagement = ({ API }) => {
  const { roomTypes } = useRoomTypes(API);
  const { rooms } = useRooms(API);
  const {
    rates,
    loading,
    selectedTarget,
    fetchRates,
    createRate,
    updateRate,
    deleteRate,
    clearAllRates,
    syncPricesFromBeds24,
    fetchSyncedRates
  } = useRates(API);

  const [showRateDialog, setShowRateDialog] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  const [showSyncDialog, setShowSyncDialog] = useState(false);
  const [showSyncedRatesDialog, setShowSyncedRatesDialog] = useState(false);
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [syncedRates, setSyncedRates] = useState([]);
  const [syncedRatesLoading, setSyncedRatesLoading] = useState(false);
  const [selectedRoomTypeFilter, setSelectedRoomTypeFilter] = useState('all');
  const [exportLoading, setExportLoading] = useState(false);
  
  const [rateForm, setRateForm] = useState({
    start_date: '',
    end_date: '',
    price: ''
  });

  const [syncDateRange, setSyncDateRange] = useState({
    date_from: '2025-01-01',
    date_to: '2026-12-31'
  });

  const handleSelectRoomType = (roomTypeId) => {
    const targetId = `type-${roomTypeId}`;
    fetchRates(targetId);
  };

  const handleEditRate = (rate) => {
    setEditingRate(rate.id);
    setRateForm({
      start_date: rate.start_date,
      end_date: rate.end_date,
      price: rate.price.toString()
    });
    setShowRateDialog(true);
  };

  const handleDeleteRate = async (rateId) => {
    if (!window.confirm('Are you sure you want to delete this rate?')) return;
    await deleteRate(rateId);
  };

  const handleSubmitRate = async () => {
    if (editingRate) {
      await updateRate(editingRate, rateForm);
    } else {
      await createRate(rateForm, selectedTarget);
    }
    setShowRateDialog(false);
    resetRateForm();
  };

  const resetRateForm = () => {
    setEditingRate(null);
    setRateForm({
      start_date: '',
      end_date: '',
      price: ''
    });
  };

  const handleSyncPrices = async () => {
    setShowSyncDialog(false);
    await syncPricesFromBeds24(syncDateRange);
  };

  const handleViewSyncedRates = async () => {
    setSyncedRatesLoading(true);
    const result = await fetchSyncedRates();
    if (result.success) {
      setSyncedRates(result.data);
      setShowSyncedRatesDialog(true);
    }
    setSyncedRatesLoading(false);
  };

  const handleClearRates = async () => {
    setShowClearDialog(false);
    await clearAllRates();
  };

  const handleExportToExcel = async () => {
    setExportLoading(true);
    try {
      const response = await apiService.get('/admin/export/rates/excel', {
        responseType: 'blob'
      });
      
      // Create blob and download
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from response headers or use default
      const contentDisposition = response.headers?.['content-disposition'];
      const filenameMatch = contentDisposition?.match(/filename=(.+)/);
      const filename = filenameMatch ? filenameMatch[1] : `rates_export_${new Date().toISOString().slice(0,10)}.xlsx`;
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Rates exported successfully!');
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export rates to Excel');
    } finally {
      setExportLoading(false);
    }
  };

  const getSelectedRoomTypeName = () => {
    if (!selectedTarget) return '';
    const typeId = selectedTarget.replace('type-', '');
    const roomType = roomTypes.find(rt => rt.id === typeId);
    return roomType?.name || '';
  };

  // Filter synced rates by room type
  const filteredSyncedRates = selectedRoomTypeFilter === 'all'
    ? syncedRates
    : syncedRates.filter(rate => {
        // If rate has room_type_id, use it directly
        if (rate.room_type_id) {
          return rate.room_type_id === selectedRoomTypeFilter;
        }
        // If rate has room_id, look up the room's type
        if (rate.room_id) {
          const room = rooms.find(r => r.id === rate.room_id);
          return room && room.room_type_id === selectedRoomTypeFilter;
        }
        return false;
      });

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold mb-2">Rate Management</h2>
          <p className="text-sm text-gray-600">Manage and sync room prices from Beds24</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button 
            onClick={handleExportToExcel}
            variant="outline"
            size="sm"
            disabled={exportLoading}
            className="border-emerald-600 text-emerald-600 hover:bg-emerald-50"
            data-testid="export-rates-excel-btn"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            {exportLoading ? 'Exporting...' : 'Export to Excel'}
          </Button>
          <Button 
            onClick={handleViewSyncedRates}
            variant="outline"
            size="sm"
            disabled={syncedRatesLoading}
            className="border-blue-600 text-blue-600 hover:bg-blue-50"
          >
            <Eye className="w-4 h-4 mr-2" />
            {syncedRatesLoading ? 'Loading...' : 'View Synced Rates'}
          </Button>
          <Button 
            onClick={() => setShowSyncDialog(true)}
            variant="outline"
            size="sm"
            className="border-green-600 text-green-600 hover:bg-green-50"
          >
            <Download className="w-4 h-4 mr-2" />
            Sync from Beds24
          </Button>
          <Button 
            onClick={() => setShowClearDialog(true)}
            variant="outline"
            size="sm"
            className="border-red-600 text-red-600 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Clear All Rates
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Room Types Selection */}
        <div className="lg:col-span-1">
          <div className="mb-4">
            <h3 className="font-semibold">Select Room Type</h3>
          </div>
          <div className="space-y-2">
            {roomTypes.map((type) => (
              <div
                key={type.id}
                className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  selectedTarget === `type-${type.id}` 
                    ? 'border-blue-600 bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
                onClick={() => handleSelectRoomType(type.id)}
              >
                <div className="font-semibold text-sm">{type.name}</div>
                <div className="text-xs text-gray-500">
                  Base price: {formatCurrency(type.base_price)}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Rates Display */}
        <div className="lg:col-span-2">
          {selectedTarget ? (
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="text-lg">
                    Rates for {getSelectedRoomTypeName()}
                  </CardTitle>
                  <Button 
                    size="sm" 
                    className="bg-gradient-to-r from-blue-600 to-indigo-600"
                    onClick={() => setShowRateDialog(true)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Rate
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {rates.length === 0 ? (
                  <p className="text-gray-500 text-center py-8 text-sm">
                    No custom rates set for this room type
                  </p>
                ) : (
                  <div className="space-y-2">
                    {rates.map((rate) => (
                      <div 
                        key={rate.id} 
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <CalendarIcon className="w-4 h-4 text-gray-500" />
                            <span className="font-medium text-sm">
                              {formatDate(rate.start_date)} → {formatDate(rate.end_date)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Euro className="w-4 h-4 text-blue-600" />
                            <span className="text-lg font-bold text-blue-600">
                              {formatCurrency(rate.price)}/night
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleEditRate(rate)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleDeleteRate(rate.id)}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-12 text-center">
                <p className="text-gray-500">Select a room type to manage its rates</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Rate Form Dialog */}
      <FormDialog
        open={showRateDialog}
        onOpenChange={(open) => {
          setShowRateDialog(open);
          if (!open) resetRateForm();
        }}
        title={editingRate ? 'Edit Rate' : 'Add Rate'}
        onSubmit={handleSubmitRate}
        submitLabel={editingRate ? 'Update Rate' : 'Create Rate'}
      >
        <FormField label="Start Date" required htmlFor="start_date">
          <Input
            id="start_date"
            type="date"
            value={rateForm.start_date}
            onChange={(e) => setRateForm({ ...rateForm, start_date: e.target.value })}
            required={!editingRate}
          />
        </FormField>

        <FormField label="End Date" required htmlFor="end_date">
          <Input
            id="end_date"
            type="date"
            value={rateForm.end_date}
            onChange={(e) => setRateForm({ ...rateForm, end_date: e.target.value })}
            required={!editingRate}
          />
        </FormField>

        <FormField label="Price (€)" required htmlFor="price">
          <Input
            id="price"
            type="number"
            step="0.01"
            value={rateForm.price}
            onChange={(e) => setRateForm({ ...rateForm, price: e.target.value })}
            required={!editingRate}
          />
        </FormField>
      </FormDialog>

      {/* Sync Prices Dialog */}
      <Dialog open={showSyncDialog} onOpenChange={setShowSyncDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="w-5 h-5 text-green-600" />
              Sync Prices from Beds24
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Select the date range to sync room prices from Beds24.
            </p>
            <FormField label="From Date" required>
              <Input
                type="date"
                value={syncDateRange.date_from}
                onChange={(e) => setSyncDateRange({ ...syncDateRange, date_from: e.target.value })}
              />
            </FormField>
            <FormField label="To Date" required>
              <Input
                type="date"
                value={syncDateRange.date_to}
                onChange={(e) => setSyncDateRange({ ...syncDateRange, date_to: e.target.value })}
              />
            </FormField>
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-sm text-green-800">
                ℹ️ <strong>What happens:</strong>
              </p>
              <ul className="text-sm text-green-700 list-disc list-inside mt-1 space-y-1">
                <li>Fetches website prices from Beds24</li>
                <li>Creates/updates local rate overrides</li>
                <li>Rates marked with source: "beds24_sync"</li>
              </ul>
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setShowSyncDialog(false)}
              >
                Cancel
              </Button>
              <Button
                className="bg-gradient-to-r from-green-600 to-emerald-600"
                onClick={handleSyncPrices}
              >
                <Download className="w-4 h-4 mr-2" />
                Sync Prices
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Synced Rates Viewer Dialog */}
      <Dialog open={showSyncedRatesDialog} onOpenChange={setShowSyncedRatesDialog}>
        <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Synced Rates from Beds24</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {syncedRates.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">No synced rates found.</p>
              </div>
            ) : (
              <>
                {/* Room Type Filter */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <Label className="text-sm font-medium whitespace-nowrap">
                      Filter by Room Type:
                    </Label>
                    <span className="text-xs text-gray-600">
                      Showing {filteredSyncedRates.length} of {syncedRates.length} rates
                    </span>
                  </div>
                  <select
                    value={selectedRoomTypeFilter}
                    onChange={(e) => setSelectedRoomTypeFilter(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md text-sm"
                  >
                    <option value="all">All Room Types</option>
                    {roomTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Display synced rates - Compact view */}
                <div className="space-y-1">
                  {filteredSyncedRates.map((rate, index) => (
                    <div key={index} className="px-3 py-2 border rounded hover:bg-gray-50 transition-colors">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-4">
                          <span className="font-medium text-gray-900 min-w-[100px]">{formatDate(rate.date)}</span>
                          <span className="text-blue-600 font-semibold">{formatCurrency(rate.price)}</span>
                        </div>
                        <div className="text-xs text-gray-500">
                          Synced: {formatDate(rate.synced_at)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Clear Rates Confirmation */}
      <Dialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Clear All Rate Overrides
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800 font-medium mb-2">
                ⚠️ Warning: This action cannot be undone!
              </p>
              <p className="text-sm text-red-700">
                This will permanently delete all rate overrides, including manually created and synced rates.
              </p>
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setShowClearDialog(false)}
              >
                Cancel
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700"
                onClick={handleClearRates}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Yes, Clear All Rates
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};